import React from "react";
import { Box, List, ListItem, ListItemText, makeStyles, Typography } from "@material-ui/core";
import { ChevronRight } from "@material-ui/icons";
import { FormattedMessage } from "react-intl";
import ContractEarnings from "./ContractEarnings";
import CommissionsEarnings from "./CommissionsEarnings";
import PageContainer from "../../../../custom/Page";
import HeaderPageItem from "../../../../custom/HeaderPageItem";
import UserEarningsInfo from "./UserEarningsInfo";
import SectionCollapse from "../../SectionCollapse";
import YearSelector from "../../YearSelector";
import AnimatedCardContent from "../../AnimatedCardContent";

const useStyles = makeStyles((theme) => ({
  titleContainer: {
    minHeight: 64,
    textAlign: "center",
  },
  title: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

const BASE_YEAR = new Date().getFullYear() - 1;

const EarningsReport = ({ getContractEarnings, getCommissionsEarnings, currentUser, history, user, match }) => {
  const classes = useStyles();
  const [selectedSection, setSelectedSection] = React.useState(null);
  const [year, setYear] = React.useState(() => new Date().getFullYear() - 1);
  const yearMessage = selectedSection?.key === "commissions" ? "app.earning.choice.commission.year" : "app.earning.choice.year";

  const SECTIONS = React.useMemo(
    () => [
      {
        action: getContractEarnings,
        component: ContractEarnings,
        key: "open-contract",
        label: "app.earnings.choiceButton.open-contract",
        type: "OPEN",
      },
      {
        action: getContractEarnings,
        component: ContractEarnings,
        key: "closed-contract",
        label: "app.earnings.choiceButton.closed-contract",
        type: "CLOSED",
      },
      {
        action: getCommissionsEarnings,
        component: CommissionsEarnings,
        key: "commissions",
        label: "app.earnings.choiceButton.commissions",
        type: "COMMISSION",
      },
    ],
    [getCommissionsEarnings, getContractEarnings],
  );

  const handleSelectSection = (section) => {
    if (section) {
      setYear(BASE_YEAR);
      setSelectedSection(section);
      return;
    }

    selectedSection ? setSelectedSection(null) : history.goBack();
  };

  const SelectedComponent = selectedSection?.component;

  return (
    <PageContainer>
      <HeaderPageItem
        title="app.earnings"
        onBack={history.length > 2 || selectedSection ? () => handleSelectSection(null) : null}
      />

      <SectionCollapse in={!!selectedSection}>
        <YearSelector
          key={year}
          year={year}
          setYear={setYear}
          message={yearMessage}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="100%"
          className={classes.titleContainer}
        >
          <Typography
            variant="h5"
            color="primary"
          >
            {selectedSection && <FormattedMessage id={`app.earnings.choiceButton.${selectedSection.key}`} />}
          </Typography>
        </Box>
      </SectionCollapse>

      <UserEarningsInfo
        user={user}
        currentUser={currentUser}
      />

      {!selectedSection ? (
        <AnimatedCardContent>
          <Typography
            gutterBottom
            variant="h6"
            color="primary"
          >
            <FormattedMessage id="app.earnings.choice" />
          </Typography>
          <Typography
            variant="body2"
            gutterBottom
          >
            <FormattedMessage id="app.earnings.choice.description" />
          </Typography>
          <List>
            {SECTIONS.map((section) => (
              <ListItem
                button
                key={section.key}
                onClick={() => handleSelectSection(section)}
              >
                <ListItemText primary={<FormattedMessage id={section.label} />} />
                <ChevronRight />
              </ListItem>
            ))}
          </List>
        </AnimatedCardContent>
      ) : (
        SelectedComponent && (
          <SelectedComponent
            currentUser={currentUser}
            user={user}
            year={year}
            type={selectedSection.type}
            getEarnings={selectedSection.action}
          />
        )
      )}
    </PageContainer>
  );
};

export default EarningsReport;
